import type { ReactNode } from 'react'
import { Download, ExternalLink, Eye, X } from 'lucide-react'
import { motion } from 'framer-motion'

interface DocumentPreviewDialogProps {
  title: string
  subtitle?: string | null
  fileUrl?: string | null
  onClose: () => void
  children?: ReactNode
  footer?: ReactNode
}

export default function DocumentPreviewDialog({
  title,
  subtitle,
  fileUrl,
  onClose,
  children,
  footer
}: DocumentPreviewDialogProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-dark-border bg-dark-surface"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-dark-border px-6 py-5">
          <div className="min-w-0">
            <h2 className="truncate text-xl font-semibold text-white">{title}</h2>
            {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
          </div>

          <button onClick={onClose} className="btn-ghost" type="button" aria-label="Fermer l apercu">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {fileUrl ? (
            <>
              <div className="mb-4 flex flex-wrap gap-2">
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  <ExternalLink className="h-4 w-4" />
                  Ouvrir le PDF
                </a>

                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="btn-secondary"
                >
                  <Download className="h-4 w-4" />
                  Telecharger
                </a>
              </div>

              <iframe
                src={fileUrl}
                title={title}
                className="h-[62vh] w-full rounded-2xl border border-dark-border bg-slate-950"
              />
            </>
          ) : (
            <div className="flex h-64 items-center justify-center rounded-2xl border border-dark-border bg-dark-surface-high text-slate-400">
              <div className="flex items-center gap-3">
                <Eye className="h-5 w-5" />
                <span>Aucun apercu PDF disponible</span>
              </div>
            </div>
          )}

          {children && <div className="mt-6">{children}</div>}
        </div>

        {footer && <div className="border-t border-dark-border px-6 py-4">{footer}</div>}
      </motion.div>
    </motion.div>
  )
}
